import { Player, Area } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Monitor, Smartphone, Laptop, ChevronDown, ChevronRight } from "lucide-react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { DashboardPlayerHealthStatus } from "@/pages/dashboard/DashboardPlayerHealthStatus";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getArea } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useState, useEffect } from "react";

interface AreaNode extends Area {
  children: AreaNode[];
  level: number;
  players: Player[];
}

interface Props {
  players: Player[];
}

export const AssignedPlayersRenderer = ({ players }: Props) => {
  const { t } = useTranslation();
  const { goToPlayerDetail } = useAppNavigate();
  const activeFirm = useSelector(selectActiveFirm);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const {
    data: areaData,
    isLoading: isLoadingAreas,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ["getArea", activeFirm?.firmGuid],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
    enabled: !!activeFirm?.firmGuid,
  });

  const areas = areaData?.data?.result || [];

  // Group players by their areaintId
  const playersByArea = players.reduce((acc: Record<number, Player[]>, player) => {
    const areaId = player.areaintId;
    if (!acc[areaId]) {
      acc[areaId] = [];
    }
    acc[areaId].push(player);
    return acc;
  }, {});

  // Build tree structure from flat array
  const buildTree = (areas: Area[]): AreaNode[] => {
    const areaMap = new Map<number, AreaNode>();
    const rootNodes: AreaNode[] = [];

    // Initialize all nodes
    areas.forEach((area) => {
      areaMap.set(area.intId, {
        ...area,
        children: [],
        level: 0,
        players: playersByArea[area.intId] || [],
      });
    });

    // Build parent-child relationships
    areas.forEach((area) => {
      const node = areaMap.get(area.intId);
      if (!node) return;

      if (area.motherAreaintId && area.motherAreaintId !== 0) {
        const parent = areaMap.get(area.motherAreaintId);
        if (parent) {
          node.level = parent.level + 1;
          parent.children.push(node);
        } else {
          rootNodes.push(node);
        }
      } else {
        rootNodes.push(node);
      }
    });

    // Filter to only include nodes that have players or children with players
    const filterNodesWithPlayers = (nodes: AreaNode[]): AreaNode[] => {
      return nodes.filter((node) => {
        const hasPlayers = node.players.length > 0;
        const filteredChildren = filterNodesWithPlayers(node.children);
        node.children = filteredChildren;
        return hasPlayers || filteredChildren.length > 0;
      });
    };

    return filterNodesWithPlayers(rootNodes);
  };

  const areaTree = buildTree(areas);

  // Expand all nodes by default
  useEffect(() => {
    if (!isFetchedAfterMount) return;
    const getAllNodeIds = (nodes: AreaNode[]): number[] => {
      const nodeIds: number[] = [];
      const collectNodeIds = (nodeList: AreaNode[]) => {
        nodeList.forEach((node) => {
          nodeIds.push(node.intId);
          collectNodeIds(node.children);
        });
      };
      collectNodeIds(nodes);
      return nodeIds;
    };

    const allNodeIds = getAllNodeIds(areaTree);
    setExpandedNodes(new Set(allNodeIds));
  }, [areas, isFetchedAfterMount]);

  const toggleExpanded = (nodeId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType?.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-4 w-4 mr-2" />;
      case "tablet":
        return <Laptop className="h-4 w-4 mr-2" />;
      default:
        return <Monitor className="h-4 w-4 mr-2" />;
    }
  };

  const renderPlayer = (player: Player, areaLevel: number): React.ReactNode => {
    const indentLevel = (areaLevel + 1) * 20;

    return (
      <div key={`player-${player.intId}`} className="w-full">
        <div
          className="flex items-center py-2 hover:bg-muted/50 rounded-sm cursor-pointer"
          style={{ paddingLeft: `${indentLevel}px` }}
          onClick={() => goToPlayerDetail(player.id)}
        >
          <div className="w-4 h-4 mr-2" />
          <div className="flex items-center flex-1">
            {getDeviceIcon(player.deviceType)}
            <span className="mr-2 font-medium text-xs">{player.name}</span>
            <DashboardPlayerHealthStatus playerId={player.intId} />
            <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground mr-2">
              <span className="text-xs">{player.deviceType || t("Unknown")}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNode = (node: AreaNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const hasPlayers = node.players.length > 0;
    const hasChildrenOrPlayers = hasChildren || hasPlayers;
    const isExpanded = expandedNodes.has(node.intId);
    const indentLevel = node.level * 20;

    return (
      <div key={node.intId} className="w-full">
        <div
          className="flex items-center py-2 hover:bg-muted/50 rounded-sm"
          style={{ paddingLeft: `${indentLevel}px` }}
        >
          {hasChildrenOrPlayers ? (
            <button
              type="button"
              onClick={() => toggleExpanded(node.intId)}
              className="flex items-center justify-center w-4 h-4 mr-2 hover:bg-muted rounded-sm"
            >
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          ) : (
            <div className="w-4 h-4 mr-2" />
          )}

          <div className="flex items-center flex-1">
            <span className="font-semibold text-xs">{node.name}</span>
          </div>
        </div>

        {hasChildrenOrPlayers && isExpanded && (
          <div className="ml-4">
            {node.children.map(renderNode)}
            {node.players.map((player) => renderPlayer(player, node.level))}
          </div>
        )}
      </div>
    );
  };

  if (isLoadingAreas) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("common.assignedPlayers")}</CardTitle>
          <CardDescription>{t("common.assignedPlayersDesc", { count: players.length })}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-40 flex flex-col items-center justify-center">
            <LoadingSmall />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!players.length) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("common.assignedPlayers")}</CardTitle>
          <CardDescription>{t("common.assignedPlayersEmpty")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-40 flex flex-col items-center justify-center text-muted-foreground">
            <Monitor className="h-12 w-12 mb-2" />
            <p className="text-xs">{t("common.assignedPlayersEmpty")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (areaTree.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("common.assignedPlayers")}</CardTitle>
          <CardDescription>{t("common.assignedPlayersDesc", { count: players.length })}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-40 flex flex-col items-center justify-center text-muted-foreground">
            <Monitor className="h-12 w-12 mb-2" />
            <p className="text-xs">{t("No players found")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("common.assignedPlayers")}</CardTitle>
        <CardDescription>{t("common.assignedPlayersDesc", { count: players.length })}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 max-h-96 overflow-y-auto">{areaTree.map(renderNode)}</div>
      </CardContent>
    </Card>
  );
};
